import React, { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { billingService } from '../../services/billingService';
import { formatRubExact, parseUtc, PLAN_TITLES } from '../../lib/billing';
import { BTN_SECONDARY } from '../../lib/buttonStyles';

// «Платежи и списания» в личном кабинете. Два списка, а не один: платёж — это
// то, что человек отдал платёжной системе, а операция по балансу — то, что мы
// с этими деньгами сделали. Пополнение попадает в оба, и это правильно: так
// видно, что оплаченное действительно зачислено.


const PAYMENT_STATUS = {
  pending: 'ожидает оплаты',
  // Брошенная оплата картой: bit-billing отдаёт её так через час (Б7, 24.09).
  // Придёт оплата позже — строка сама станет «оплачен».
  expired: 'не завершён',
  succeeded: 'оплачен',
  canceled: 'отменён',
  failed: 'не создан',
};

function formatDateTime(value) {
  const d = parseUtc(value);
  if (!d) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatMonth(value) {
  const d = parseUtc(value);
  if (!d) return '';
  return d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

// Знак ставим сами: у Intl это дефис, а в сумме денег нужен минус.
function signedRub(amount) {
  const value = Number(amount) || 0;
  return `${value < 0 ? '−' : '+'}${formatRubExact(Math.abs(value))}`;
}

export function operationTitle(op) {
  if (op.kind === 'settle') return `Списание за ${formatMonth(op.month_start)}`;
  // Возврат пополнения на карту: деньги ушли с баланса обратно (bit-billing,
  // событие payment.refunded). Без своей подписи строка «−400 ₽» читалась бы
  // как «Пополнение баланса» со знаком минус.
  if (op.kind === 'refund') return 'Возврат пополнения';
  return 'Пополнение баланса';
}

// Из чего сложилось списание. Без этой строки «−300,00 ₽ за сентябрь» человеку
// нечем проверить: минуты и подарок он нигде больше задним числом не увидит.
export function operationDetails(op) {
  if (op.kind !== 'settle' || typeof op.minutes !== 'number') return null;
  const parts = [`${op.minutes} мин`];
  if (op.gift_minutes) parts.push(`${op.gift_minutes} в подарок`);
  if (op.rate_rub) parts.push(`по ${formatRubExact(op.rate_rub)}/мин`);
  return parts.join(', ');
}

// Возврат по платежу. Статус платежа остаётся «оплачен» — возврат отдельная
// запись, а не понижение статуса (bit-billing: refunded_rub копит сумму
// частичных возвратов, refund_status — partial | full).
export function paymentRefundLabel(payment) {
  const refunded = Number(payment.refunded_rub) || 0;
  if (refunded <= 0) return null;
  if (payment.refund_status === 'full' || refunded >= (Number(payment.amount_rub) || 0)) {
    return 'возвращён полностью';
  }
  return `возвращено ${formatRubExact(refunded)}`;
}

export function paymentTitle(payment) {
  if (payment.purpose === 'topup') return 'Пополнение баланса';
  // Оплата по счёту записывается админом по выписке: тарифа в записи нет,
  // и без этой строки она читалась бы «Тариф «Тариф», месяц».
  if (payment.purpose === 'invoice') {
    return payment.invoice_number ? `Оплата по счёту № ${payment.invoice_number}` : 'Оплата по счёту';
  }
  const plan = PLAN_TITLES[payment.plan_code] || payment.plan_code || 'Тариф';
  const period = payment.billing_period === 'year' ? 'год' : 'месяц';
  return `Тариф «${plan}», ${period}`;
}

// Сколько строк видно сразу и сколько добавляет «Показать ещё». Список рос
// бесконечно и выглядел лентой (тест владельца 25.09); страница бэкенда — 50.
export const FIRST_ROWS = 5;
export const MORE_ROWS = 10;
const PAGE = 50;

/**
 * Список с дозагрузкой: показывает `shown` строк, при нехватке загруженных
 * просит у бэкенда следующую страницу. total — total_count из ответа.
 */
function usePagedList(fetchPage) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [shown, setShown] = useState(FIRST_ROWS);
  const page = useRef(1);
  // Запрос следующей страницы уже идёт — второй клик его не дублирует (иначе
  // одна страница догружалась дважды и строки двоились; ревью 25.09).
  const loading = useRef(false);

  useEffect(() => {
    let alive = true;
    // Отказ не критичен для страницы: блок просто не рисуется.
    fetchPage({ page: 1, page_size: PAGE })
      .then((d) => { if (alive) { setItems(d?.items || []); setTotal(d?.total_count || 0); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [fetchPage]);

  const more = async () => {
    if (loading.current) return;
    const next = shown + MORE_ROWS;
    if (next > items.length && items.length < total) {
      loading.current = true;
      try {
        const d = await fetchPage({ page: page.current + 1, page_size: PAGE });
        const got = d?.items || [];
        page.current += 1;
        setItems((prev) => [...prev, ...got]);
        // Страница пришла неполной — дальше нечего просить, даже если total_count
        // успел вырасти или разошёлся: иначе кнопка не исчезла бы никогда.
        if (got.length < PAGE) setTotal(items.length + got.length);
      } catch (_) { /* покажем то, что уже есть */ } finally {
        loading.current = false;
      }
    }
    setShown(next);
  };

  return {
    items,
    visible: items.slice(0, shown),
    hasMore: shown < items.length || items.length < total,
    more,
  };
}

const MoreButton = ({ onClick, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick} className={`mt-2 ${BTN_SECONDARY}`}>
    Показать ещё
  </button>
);

const fetchOperations = (params) => billingService.getBalanceOperations(params);
const fetchPayments = (params) => billingService.getMyPayments(params);

export default function BillingHistory() {
  const ops = usePagedList(fetchOperations);
  const pays = usePagedList(fetchPayments);
  const operations = ops.visible;
  const payments = pays.visible;

  // Платить ещё не начинали — карточки нет вовсе, пустая таблица ничего не сообщает.
  if (ops.items.length === 0 && pays.items.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Платежи и списания</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {operations.length > 0 && (
          <div data-testid="balance-operations">
            <div className="text-xs uppercase tracking-wide text-paper-hint mb-2">Операции по балансу</div>
            <ul className="divide-y divide-gray-100">
              {operations.map((op) => (
                <li key={op.id} className="flex items-baseline justify-between gap-4 py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-paper-ink">{operationTitle(op)}</div>
                    <div className="text-xs text-paper-ink-3">
                      {formatDateTime(op.created_at)}
                      {operationDetails(op) && ` · ${operationDetails(op)}`}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-medium ${op.amount_rub < 0 ? 'text-paper-ink' : 'text-emerald-700'}`}>
                      {signedRub(op.amount_rub)}
                    </div>
                    <div className="text-xs text-paper-ink-3">
                      остаток {formatRubExact(op.balance_after_rub || 0)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {ops.hasMore && <MoreButton testId="operations-more" onClick={ops.more} />}
          </div>
        )}

        {payments.length > 0 && (
          <div data-testid="payments-list">
            <div className="text-xs uppercase tracking-wide text-paper-hint mb-2">Платежи</div>
            <ul className="divide-y divide-gray-100">
              {payments.map((payment) => (
                <li key={payment.id} className="flex items-baseline justify-between gap-4 py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-paper-ink">{paymentTitle(payment)}</div>
                    <div className="text-xs text-paper-ink-3">
                      {formatDateTime(payment.paid_at || payment.created_at)}
                      {' · '}
                      {PAYMENT_STATUS[payment.status] || payment.status}
                      {paymentRefundLabel(payment) && ` · ${paymentRefundLabel(payment)}`}
                    </div>
                  </div>
                  <div className="text-sm text-paper-ink shrink-0">
                    {formatRubExact(payment.amount_rub || 0)}
                  </div>
                </li>
              ))}
            </ul>
            {pays.hasMore && <MoreButton testId="payments-more" onClick={pays.more} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
